import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { Plus, GripVertical, Save } from "lucide-react";
import { toast } from "sonner";
import { BulkImportTrigger } from "./BulkImportFacadesModal.jsx";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { adminApi } from "../../api/admin.api.js";
import { call } from "../../api/client.js";
import { useResource } from "../../hooks/useResource.js";
import { Button } from "../../components/ui/Button.jsx";
import { Modal } from "../../components/ui/Modal.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { ImageUpload, ImageThumbnail } from "../../components/upload/ImageUpload.jsx";
import { SlugField } from "../../components/forms/SlugField.jsx";
import { Spinner } from "../../components/ui/Spinner.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { cn } from "../../lib/cn.js";

/**
 * Facades page — flat list with drag-reorder via the
 * /admin/facades/reorder bulk endpoint. Showcases the drag-and-drop
 * persistence requirement from the Tech Impl § 11 Session 9 acceptance.
 *
 * Order is local until "Save order" is clicked — persists with one
 * bulkWrite roundtrip (not per-row PATCH).
 */
export default function FacadesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [orderDirty, setOrderDirty] = useState(false);
  const [localOrder, setLocalOrder] = useState([]); // array of facade IDs

  useEffect(() => {
    document.title = "Facades · Admin";
  }, []);

  const { remove } = useResource("facades", { toastLabel: "facade" });

  const { data: facades, isLoading } = useQuery({
    queryKey: ["facades", "all"],
    queryFn: () => adminApi.list("facades", { limit: 200, sort: "sortOrder:asc" }),
    staleTime: 30_000,
  });

  // Initialize local order whenever the server data changes.
  useEffect(() => {
    if (facades?.items) {
      setLocalOrder(facades.items.map((f) => f._id));
      setOrderDirty(false);
    }
  }, [facades]);

  const reorderMutation = useMutation({
    mutationFn: (ids) => call("patch", "/admin/facades/reorder", { data: { ids } }),
    onSuccess: () => {
      toast.success("Order saved");
      setOrderDirty(false);
      queryClient.invalidateQueries({ queryKey: ["facades"] });
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? "Failed to save order"),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Build the rendered list from localOrder, applying search filter.
  const byId = new Map((facades?.items ?? []).map((f) => [f._id, f]));
  const filtered = localOrder
    .map((id) => byId.get(id))
    .filter(Boolean)
    .filter((f) => (search ? f.title.toLowerCase().includes(search.toLowerCase()) : true));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localOrder.indexOf(active.id);
    const newIndex = localOrder.indexOf(over.id);
    setLocalOrder(arrayMove(localOrder, oldIndex, newIndex));
    setOrderDirty(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Catalogue</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Facades</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {facades?.items?.length ?? "—"} facade variant
            {facades?.items?.length === 1 ? "" : "s"}. Drag to reorder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {orderDirty && (
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              loading={reorderMutation.isPending}
              onClick={() => reorderMutation.mutate(localOrder)}
            >
              Save order
            </Button>
          )}
          <BulkImportTrigger />
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({})}>
            New facade
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search facades by title…"
        />

        {isLoading ? (
          <div className="py-16 text-center">
            <Spinner size="md" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-text-muted">
            {search ? "No matches." : 'No facades yet — click "New facade" to add one.'}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
              <ul className="divide-y divide-border-subtle bg-bg-elevated">
                {filtered.map((f, i) => (
                  <FacadeRow
                    key={f._id}
                    facade={f}
                    index={i}
                    onEdit={() => setEditing(f)}
                    onDelete={() => setConfirmDelete(f)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {editing && (
        <FacadeFormModal
          row={editing._id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ["facades"] });
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.title}"?`}
        description="The facade will be removed from any designs that link to it."
        confirmLabel="Delete facade"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}

function FacadeRow({ facade, index, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: facade._id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-4 px-4 py-3 transition-colors hover:bg-bg-elevated-2",
        isDragging && "opacity-50"
      )}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="cursor-grab text-text-muted hover:text-text-primary active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="w-8 text-xs font-mono text-text-muted">#{index + 1}</span>
      <ImageThumbnail src={facade.image} alt={facade.title} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">{facade.title}</p>
        <p className="truncate text-xs text-text-muted">
          {facade.applicableDesigns?.length ?? 0} linked design
          {facade.applicableDesigns?.length === 1 ? "" : "s"}
        </p>
      </div>
      {facade.isFeatured && <Badge tone="accent">featured</Badge>}
      <RowActions onEdit={onEdit} onDelete={onDelete} />
    </li>
  );
}

function FacadeFormModal({ row, onClose, onSaved }) {
  const queryClient = useQueryClient();
  const isNew = !row;
  const form = useForm({
    defaultValues: {
      title: "",
      slug: "",
      image: "",
      thumbnail: "",
      description: "",
      isFeatured: false,
      sortOrder: 0,
      ...(row ?? {}),
    },
  });
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = form;

  // Surface the backend's per-field error details (Zod issues come back as
  // `details: [{ path, message }, …]`) rather than a generic "Validation
  // failed" toast — that's what was hiding which field was empty.
  const errToast = (label) => (err) => {
    const details = err?.response?.data?.details;
    if (Array.isArray(details) && details.length > 0) {
      toast.error(`${details[0].path}: ${details[0].message}`);
    } else {
      toast.error(err?.response?.data?.message ?? label);
    }
  };

  const create = useMutation({
    mutationFn: (data) => adminApi.create("facades", data),
    onSuccess: () => {
      toast.success("Facade created");
      queryClient.invalidateQueries({ queryKey: ["facades"] });
      onSaved();
    },
    onError: errToast("Create failed"),
  });
  const update = useMutation({
    mutationFn: (data) => adminApi.update("facades", row._id, data),
    onSuccess: () => {
      toast.success("Facade updated");
      queryClient.invalidateQueries({ queryKey: ["facades"] });
      onSaved();
    },
    onError: errToast("Update failed"),
  });

  const submit = handleSubmit((values) => {
    const payload = { ...values };
    if (!payload.slug) delete payload.slug;
    if (payload.sortOrder === "") payload.sortOrder = 0;
    if (isNew) create.mutate(payload);
    else update.mutate(payload);
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? "New facade" : `Edit "${row?.title}"`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting}>
            {isNew ? "Create facade" : "Save changes"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Title"
          required
          error={errors.title?.message}
          {...register("title", { required: "Title is required" })}
        />
        <SlugField
          value={watch("slug")}
          onChange={(v) => setValue("slug", v)}
          source={watch("title")}
        />
        <Controller
          control={control}
          name="image"
          rules={{ required: "Upload a facade image" }}
          render={({ field, fieldState }) => (
            <div>
              <ImageUpload
                label="Image"
                required
                subdir="facades"
                aspect="video"
                value={field.value}
                onChange={field.onChange}
              />
              {fieldState.error?.message && (
                <p className="mt-1 text-xs text-danger">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />
        <Textarea label="Description" rows={3} {...register("description")} />
        <div className="grid grid-cols-2 gap-4">
          <Input type="number" label="Sort order" {...register("sortOrder")} />
          <Controller
            control={control}
            name="isFeatured"
            render={({ field }) => (
              <Switch label="Featured" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        </div>
      </form>
    </Modal>
  );
}
